import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Iconify from '../common/iconify.component';
import {
  getPlatformColorCode,
  getPlatformLabel,
} from '../../config/platforms.config';

const formatNumber = (value) =>
  new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

const getPlatformDisplayLabel = (platformLabel) =>
  getPlatformLabel(platformLabel) || platformLabel || 'Unknown';

const getGroupedDisplayLabel = (label, breakdownMode) =>
  breakdownMode === 'platform' ? getPlatformDisplayLabel(label) : label;

const getGroupedColorCode = (label, breakdownMode) =>
  breakdownMode === 'platform'
    ? getPlatformColorCode(label)
    : alpha('#0B79C8', 0.72);

const filteredUserCount = (platforms = [], activePlatformSet = null) => {
  if (!platforms.length) return 0;

  return platforms
    .filter(
      (platform) => !activePlatformSet || activePlatformSet.has(platform.label)
    )
    .reduce((sum, platform) => sum + (Number(platform.value) || 0), 0);
};

const getCount = (
  item,
  activePlatformSet = null,
  hasPlatformFilter = false
) => {
  if (!hasPlatformFilter) {
    return Number(item.uniqueDevices ?? item.value) || 0;
  }

  return filteredUserCount(item.platforms, activePlatformSet);
};

const getTopPlatforms = (platforms = [], activePlatforms = [], count = 3) =>
  [...platforms]
    .filter(
      (platform) =>
        !activePlatforms.length || activePlatforms.includes(platform.label)
    )
    .sort((a, b) => b.value - a.value)
    .slice(0, count);

const LocationStatsPanel = ({
  locationsData,
  breakdownMode,
  selectedCountry,
  highlightedDotId,
  isOpen,
  activePlatforms,
  onCountrySelect,
  onCitySelect,
  onReset,
  onToggle,
  onBreakdownModeChange,
  onTogglePlatform,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const isCountryView = Boolean(selectedCountry);
  const hasPlatformFilter = activePlatforms.length > 0;
  const activePlatformSet = useMemo(
    () => (hasPlatformFilter ? new Set(activePlatforms) : null),
    [activePlatforms, hasPlatformFilter]
  );

  useEffect(() => {
    setSearchQuery('');
    setHoveredItem(null);
  }, [selectedCountry?.id]);

  useEffect(() => {
    if (!isOpen) {
      setHoveredItem(null);
    }
  }, [isOpen]);

  const rankedCountries = useMemo(
    () =>
      [...(locationsData?.countries || [])]
        .map((country) => ({
          ...country,
          filteredCount: getCount(
            country,
            activePlatformSet,
            hasPlatformFilter
          ),
        }))
        .filter((country) => country.filteredCount > 0)
        .sort((a, b) => b.filteredCount - a.filteredCount),
    [activePlatformSet, hasPlatformFilter, locationsData]
  );

  const rankedCities = useMemo(() => {
    if (!selectedCountry) return [];

    return [...selectedCountry.cities]
      .map((city) => ({
        ...city,
        filteredCount: getCount(city, activePlatformSet, hasPlatformFilter),
      }))
      .filter((city) => city.filteredCount > 0)
      .sort((a, b) => b.filteredCount - a.filteredCount);
  }, [activePlatformSet, hasPlatformFilter, selectedCountry]);

  const items = isCountryView ? rankedCities : rankedCountries;
  const filteredItems = searchQuery
    ? items.filter((item) =>
        String(item.name || '')
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : items;

  const maxCount = filteredItems[0]?.filteredCount || 1;

  const totalUsers = useMemo(() => {
    if (selectedCountry) {
      return getCount(selectedCountry, activePlatformSet, hasPlatformFilter);
    }

    return (locationsData?.countries || []).reduce(
      (sum, country) =>
        sum + getCount(country, activePlatformSet, hasPlatformFilter),
      0
    );
  }, [activePlatformSet, hasPlatformFilter, locationsData, selectedCountry]);

  const aggregatedPlatforms = useMemo(() => {
    const source = selectedCountry
      ? selectedCountry.cities
      : locationsData?.countries || [];
    const aggregate = {};

    source.forEach((item) => {
      (item.platforms || []).forEach((platform) => {
        aggregate[platform.label] =
          (aggregate[platform.label] || 0) + (Number(platform.value) || 0);
      });
    });

    if (!Object.keys(aggregate).length && selectedCountry?.platforms?.length) {
      selectedCountry.platforms.forEach((platform) => {
        aggregate[platform.label] = Number(platform.value) || 0;
      });
    }

    return Object.keys(aggregate)
      .map((label) => ({ label, value: aggregate[label] }))
      .filter(
        (platform) =>
          !activePlatformSet || activePlatformSet.has(platform.label)
      )
      .sort((a, b) => b.value - a.value);
  }, [activePlatformSet, locationsData, selectedCountry]);

  const maxPlatformValue = aggregatedPlatforms[0]?.value || 1;
  const visibleCount = filteredItems.length;
  const totalCount = items.length;
  const accentColor = theme.palette.primary.main;
  const accentLight = theme.palette.primary.light;
  const surfaceBorder = alpha(theme.palette.grey[500], 0.18);
  const softSurface = alpha(theme.palette.common.white, 0.9);
  const mutedText = theme.palette.text.secondary;

  return (
    <>
      {!isOpen && (
        <IconButton
          onClick={onToggle}
          sx={{
            position: 'absolute',
            top: 110,
            right: 18,
            zIndex: 4,
            width: 44,
            height: 44,
            color: accentColor,
            bgcolor: alpha(theme.palette.common.white, 0.96),
            border: `1px solid ${surfaceBorder}`,
            boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.12)}`,
            '&:hover': {
              bgcolor: theme.palette.common.white,
            },
          }}
          title="Show rankings"
        >
          <Iconify icon="solar:chart-linear" width={18} />
        </IconButton>
      )}

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 7,
          width: 320,
          height: '100%',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease-in-out',
        }}
      >
        <Stack
          spacing={0}
          sx={{
            height: '100%',
            bgcolor: alpha(theme.palette.common.white, 0.92),
            backgroundImage: `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.06
            )} 0%, ${alpha(theme.palette.common.white, 0.96)} 18%, ${alpha(
              theme.palette.common.white,
              0.98
            )} 100%)`,
            backdropFilter: 'blur(18px)',
            borderLeft: `1px solid ${surfaceBorder}`,
            boxShadow: `-10px 0 28px ${alpha(
              theme.palette.common.black,
              0.08
            )}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: 1.8,
              py: 1.35,
              borderBottom: `1px solid ${surfaceBorder}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px',
                  color: accentColor,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  boxShadow: `inset 0 0 0 1px ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )}`,
                }}
              >
                <Iconify
                  icon={
                    isCountryView
                      ? 'solar:map-point-wave-linear'
                      : 'solar:global-linear'
                  }
                  width={16}
                />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  {isCountryView
                    ? `${selectedCountry.name} Cities`
                    : 'Country Rankings'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: mutedText,
                    letterSpacing: 0.1,
                    fontSize: 11,
                    display: 'block',
                    mt: 0.15,
                  }}
                >
                  {isCountryView
                    ? `Explore city-level activity by ${breakdownMode}`
                    : `Browse activity by country and drill into ${breakdownMode} details`}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              size="small"
              onClick={onToggle}
              sx={{
                color: mutedText,
                bgcolor: alpha(theme.palette.grey[500], 0.08),
                border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[500], 0.14),
                },
              }}
            >
              <Iconify icon="mdi:chevron-right" width={18} />
            </IconButton>
          </Stack>

          {isCountryView && (
            <Box
              onClick={onReset}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mx: 1.5,
                mt: 1.1,
                px: 1.25,
                py: 0.9,
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                color: accentColor,
                cursor: 'pointer',
                transition: 'all 180ms ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Iconify icon="solar:global-linear" width={15} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Back to Global View
              </Typography>
            </Box>
          )}

          <Box sx={{ px: 1.5, pt: 1.1, pb: 0.8 }}>
            <Stack spacing={0.9} sx={{ mb: 1 }}>
              <Stack
                direction="row"
                spacing={0.7}
                sx={{
                  p: 0.5,
                  borderRadius: '14px',
                  border: `1px solid ${surfaceBorder}`,
                  bgcolor: alpha(theme.palette.common.white, 0.84),
                }}
              >
                {[
                  { value: 'platform', label: 'Platform', icon: 'mdi:devices' },
                  {
                    value: 'language',
                    label: 'Language',
                    icon: 'mdi:translate',
                  },
                ].map((option) => (
                  <Box
                    key={option.value}
                    onClick={() => onBreakdownModeChange(option.value)}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.8,
                      px: 1.1,
                      py: 0.9,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      color:
                        breakdownMode === option.value
                          ? accentColor
                          : mutedText,
                      bgcolor:
                        breakdownMode === option.value
                          ? alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                      border:
                        breakdownMode === option.value
                          ? `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.14
                            )}`
                          : '1px solid transparent',
                      transition: 'all 180ms ease',
                    }}
                  >
                    <Iconify icon={option.icon} width={14} />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 12, fontWeight: 700 }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              sx={{
                px: 1,
                py: 0.75,
                borderRadius: '12px',
                bgcolor: alpha(theme.palette.grey[500], 0.06),
                border: `1px solid ${surfaceBorder}`,
                boxShadow: `inset 0 1px 0 ${alpha(
                  theme.palette.common.white,
                  0.6
                )}`,
              }}
            >
              <Iconify
                icon="solar:magnifer-linear"
                width={15}
                color={mutedText}
              />
              <InputBase
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={
                  isCountryView ? 'Search cities...' : 'Search countries...'
                }
                sx={{
                  ml: 0.8,
                  flex: 1,
                  fontSize: 12.5,
                  lineHeight: 1.2,
                  color: theme.palette.text.primary,
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ px: 1.5, pb: 1 }}>
            <Box
              onClick={() => setFiltersOpen((previous) => !previous)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 1.15,
                py: 0.8,
                borderRadius: '12px',
                color: mutedText,
                cursor: 'pointer',
                border: `1px solid ${surfaceBorder}`,
                bgcolor: softSurface,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.dark,
                  }}
                >
                  <Iconify icon="mdi:filter-outline" width={12} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      lineHeight: 1.1,
                    }}
                  >
                    {`${breakdownMode} Filters`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.2 }}
                  >
                    {`${activePlatforms.length} of ${locationsData.platformLabels.length} selected`}
                  </Typography>
                </Box>
              </Stack>
              <Iconify
                icon={filtersOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                width={18}
              />
            </Box>

            {filtersOpen && (
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={0.8}
                sx={{
                  mt: 0.9,
                  p: 0.5,
                  maxHeight: 124,
                  overflowY: 'auto',
                  alignContent: 'flex-start',
                  borderRadius: '14px',
                  border: `1px solid ${surfaceBorder}`,
                  bgcolor: alpha(theme.palette.common.white, 0.72),
                  pr: 0.3,
                  '&::-webkit-scrollbar': {
                    width: 6,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.35),
                    borderRadius: 999,
                  },
                }}
              >
                {locationsData.platformLabels.map((platformLabel) => (
                  <Box
                    key={platformLabel}
                    onClick={() => onTogglePlatform(platformLabel)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.3,
                      cursor: 'pointer',
                      px: 0.35,
                      py: 0.2,
                      borderRadius: '999px',
                      border: `1px solid ${alpha(
                        getGroupedColorCode(platformLabel, breakdownMode),
                        activePlatforms.includes(platformLabel) ? 0.22 : 0.12
                      )}`,
                      bgcolor: activePlatforms.includes(platformLabel)
                        ? alpha(
                            getGroupedColorCode(platformLabel, breakdownMode),
                            0.08
                          )
                        : alpha(theme.palette.common.white, 0.85),
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={activePlatforms.includes(platformLabel)}
                      sx={{
                        color: getGroupedColorCode(
                          platformLabel,
                          breakdownMode
                        ),
                        '&.Mui-checked': {
                          color: getGroupedColorCode(
                            platformLabel,
                            breakdownMode
                          ),
                        },
                        p: 0.2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 11.5, fontWeight: 600, pr: 0.55 }}
                    >
                      {getGroupedDisplayLabel(platformLabel, breakdownMode)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              mx: 1.5,
              mb: 0.4,
              px: 1.3,
              py: 1.2,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              boxShadow: `0 10px 24px ${alpha(
                theme.palette.primary.main,
                0.08
              )}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: mutedText,
                    textTransform: 'uppercase',
                    letterSpacing: 0.9,
                    fontWeight: 700,
                    fontSize: 10.5,
                    lineHeight: 1.1,
                  }}
                >
                  {isCountryView
                    ? `${selectedCountry.name} Visits`
                    : 'Total Visits'}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    color: accentLight,
                    fontWeight: 800,
                    mt: 0.35,
                    fontSize: '1.95rem',
                    lineHeight: 1,
                  }}
                >
                  {formatNumber(totalUsers)}
                </Typography>
              </Box>

              <Chip
                size="small"
                label={isCountryView ? 'City View' : 'Global View'}
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.72),
                  color: accentColor,
                  fontWeight: 700,
                  height: 24,
                  fontSize: 11.5,
                  border: `1px solid ${alpha(
                    theme.palette.primary.main,
                    0.12
                  )}`,
                }}
              />
            </Stack>

            <Stack spacing={0.7} sx={{ mt: 1.05 }}>
              {aggregatedPlatforms.slice(0, 5).map((platform) => (
                <Stack
                  key={platform.label}
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                >
                  <Typography
                    variant="caption"
                    sx={{ minWidth: 70, color: mutedText, fontSize: 10 }}
                  >
                    {getGroupedDisplayLabel(platform.label, breakdownMode)}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: 999,
                      bgcolor: alpha(theme.palette.common.black, 0.08),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${(platform.value / maxPlatformValue) * 100}%`,
                        height: '100%',
                        borderRadius: 999,
                        backgroundImage: `linear-gradient(90deg, ${accentColor} 0%, ${accentLight} 100%)`,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 28,
                      textAlign: 'right',
                      color: mutedText,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {formatNumber(platform.value)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: 1,
              pb: 1,
              '&::-webkit-scrollbar': {
                width: 7,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.grey[500], 0.35),
                borderRadius: 999,
              },
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 0.7, py: 0.75 }}
            >
              <Typography
                variant="overline"
                sx={{ color: mutedText, fontSize: 10 }}
              >
                {isCountryView ? 'Top Cities' : 'Top Countries'}
              </Typography>
              <Typography variant="caption" sx={{ color: mutedText }}>
                {`${visibleCount}${
                  visibleCount !== totalCount ? ` / ${totalCount}` : ''
                } shown`}
              </Typography>
            </Stack>

            {filteredItems.map((item, index) => {
              const itemKey = item.id;
              const isSelected = isCountryView
                ? highlightedDotId === itemKey
                : false;
              const itemCount =
                item.filteredCount ??
                getCount(item, activePlatformSet, hasPlatformFilter);
              const barWidth = (itemCount / maxCount) * 100;
              const topPlatforms = getTopPlatforms(
                item.platforms,
                activePlatforms
              );

              return (
                <Box
                  key={itemKey}
                  onClick={() => {
                    setHoveredItem(null);
                    if (isCountryView) {
                      onCitySelect(item);
                      return;
                    }

                    onCountrySelect(item);
                  }}
                  onMouseEnter={(event) => {
                    const rect = event.currentTarget.getBoundingClientRect();
                    setHoveredItem({
                      label: item.name,
                      count: itemCount,
                      platforms: item.platforms || [],
                      x: rect.left - 10,
                      y: rect.top + rect.height / 2,
                    });
                  }}
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    px: 1.3,
                    py: 1.1,
                    mb: 0.8,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    border: isSelected
                      ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      : `1px solid ${surfaceBorder}`,
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.common.white, 0.82),
                    boxShadow: isSelected
                      ? `0 12px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                      : `0 8px 20px ${alpha(theme.palette.common.black, 0.04)}`,
                    transition: 'all 220ms ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.96),
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                      transform: 'translateY(-1px)',
                      boxShadow: `0 14px 28px ${alpha(
                        theme.palette.common.black,
                        0.08
                      )}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      insetY: 0,
                      left: 0,
                      width: `${barWidth}%`,
                      bgcolor: alpha(theme.palette.primary.main, 0.07),
                    }}
                  />

                  <Stack
                    direction="row"
                    spacing={1.2}
                    alignItems="flex-start"
                    sx={{ position: 'relative' }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        color: isSelected ? accentColor : mutedText,
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.12)
                          : alpha(theme.palette.grey[500], 0.08),
                        borderRadius: '10px',
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Typography>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isSelected
                            ? accentColor
                            : theme.palette.text.primary,
                          fontWeight: isSelected ? 700 : 600,
                          fontSize: 13,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.name}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          columnGap: 0.45,
                          rowGap: 0.35,
                          mt: 0.4,
                        }}
                      >
                        {topPlatforms.map((platform) => (
                          <Typography
                            key={`${itemKey}-${platform.label}`}
                            variant="caption"
                            sx={{
                              fontSize: 9.5,
                              color: mutedText,
                              px: 0.6,
                              py: 0.15,
                              borderRadius: '999px',
                              bgcolor: alpha(
                                getGroupedColorCode(
                                  platform.label,
                                  breakdownMode
                                ),
                                0.08
                              ),
                            }}
                          >
                            {`${getGroupedDisplayLabel(
                              platform.label,
                              breakdownMode
                            )}: ${formatNumber(platform.value)}`}
                          </Typography>
                        ))}
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.text.primary,
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        alignSelf: 'center',
                      }}
                    >
                      {formatNumber(itemCount)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}

            {filteredItems.length === 0 && (
              <Typography
                variant="body2"
                sx={{ color: mutedText, textAlign: 'center', py: 4 }}
              >
                No results found
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {hoveredItem &&
        isOpen &&
        createPortal(
          <Box
            sx={{
              position: 'fixed',
              left: hoveredItem.x,
              top: hoveredItem.y,
              transform: 'translate(-100%, -50%)',
              zIndex: 1500,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                minWidth: 220,
                px: 2,
                py: 1.6,
                borderRadius: '16px',
                bgcolor: alpha(theme.palette.common.white, 0.98),
                border: `1px solid ${surfaceBorder}`,
                boxShadow: `0 18px 40px ${alpha(
                  theme.palette.common.black,
                  0.18
                )}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 1.1 }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                >
                  {hoveredItem.label}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: accentColor }}
                >
                  {formatNumber(hoveredItem.count)}
                </Typography>
              </Stack>

              <Divider />

              <Box
                sx={{
                  height: 1,
                  bgcolor: alpha(theme.palette.grey[500], 0.28),
                  mb: 1.1,
                }}
              />

              <Stack spacing={0.55}>
                {hoveredItem.platforms
                  .filter(
                    (platform) =>
                      !activePlatforms.length ||
                      activePlatforms.includes(platform.label)
                  )
                  .sort((a, b) => b.value - a.value)
                  .map((platform) => (
                    <Stack
                      key={`${hoveredItem.label}-${platform.label}`}
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: mutedText, fontSize: 12 }}
                      >
                        {getGroupedDisplayLabel(platform.label, breakdownMode)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {formatNumber(platform.value)}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </Box>
          </Box>,
          document.body
        )}
    </>
  );
};

LocationStatsPanel.propTypes = {
  locationsData: PropTypes.object.isRequired,
  breakdownMode: PropTypes.oneOf(['platform', 'language']).isRequired,
  selectedCountry: PropTypes.object,
  highlightedDotId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  activePlatforms: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCountrySelect: PropTypes.func.isRequired,
  onCitySelect: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onBreakdownModeChange: PropTypes.func.isRequired,
  onTogglePlatform: PropTypes.func.isRequired,
};

LocationStatsPanel.defaultProps = {
  selectedCountry: null,
  highlightedDotId: null,
};

export default LocationStatsPanel;
